// TODO: delete after moving to One Chat
import { useEffect, useMemo, useState } from 'react';

import {
  Sender,
  MessageType,
} from '@wix/ambassador-innovation-widget-v1-message/types';
import {
  useEnvironment,
  useTranslation,
  useExperiments,
} from '@wix/yoshi-flow-editor';

import {
  addMessage,
  AddMessagePayload,
  selectConversationsQuota,
  selectFlags,
  selectMessages,
  selectSettings,
  selectComposed,
} from '@/features';
import { useAppDispatch, useAppSelector } from '@/store';
import { EXPERIMENTS } from '@/constants';

export const useIntroSequence = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isEditor } = useEnvironment();
  const { experiments } = useExperiments();
  const [isSequenceFulfilled, setIsSequenceFulfilled] = useState(true);
  const settingsLoaded = useAppSelector(selectSettings.isLoaded);
  const messagesLoaded = useAppSelector(selectMessages.isLoaded);
  const quotaLoaded = useAppSelector(selectConversationsQuota.isLoaded);

  const introMessage = useAppSelector(selectComposed.introMessage);
  const legalMessage = useAppSelector(selectComposed.legalMessage);
  const offlineMessage = useAppSelector(selectComposed.offlineMessage);
  const offlineShouldShowContactForm = useAppSelector(
    selectComposed.offlineShouldShowContactForm,
  );
  const suggestedQuestions = useAppSelector(selectComposed.suggestions);
  const suggestionsIntroMessage = useAppSelector(
    selectComposed.suggestionsIntroMessage,
  );
  const maybeSendIntro = useAppSelector(selectMessages.shouldSendIntro);
  const maybeSendLegal = useAppSelector(selectMessages.shouldSendLegal);
  const maybeSendOfflineMessage = useAppSelector(
    selectMessages.shouldSendOfflineMessage,
  );
  const maybeSendSuggestions = useAppSelector(
    selectMessages.shouldSendSuggestions,
  );

  const isContact = useAppSelector(selectFlags.isContact);
  const noContactFormOnLastPage = useAppSelector(
    selectMessages.noContactFormOnLastPage,
  );

  const shouldFullfillSequence = useMemo(
    () => settingsLoaded && messagesLoaded && quotaLoaded,
    [settingsLoaded, messagesLoaded, quotaLoaded],
  );

  const shouldSendIntro = useMemo(
    () => Boolean(maybeSendIntro && introMessage),
    [maybeSendIntro, introMessage],
  );

  const shouldSendLegal = useMemo(
    () => Boolean(maybeSendLegal && legalMessage),
    [maybeSendLegal, legalMessage],
  );

  const shouldSendOfflineMessage = useMemo(
    () => Boolean(maybeSendOfflineMessage && offlineMessage),
    [maybeSendOfflineMessage, offlineMessage],
  );

  const shouldSendContact = useMemo(
    () => !isContact && noContactFormOnLastPage && offlineShouldShowContactForm,
    [isContact, noContactFormOnLastPage, offlineShouldShowContactForm],
  );

  const isSuggestedIntroQuestionsEnabled = experiments.enabled(
    EXPERIMENTS.SUGGESTED_QUESTIONS,
  );

  const shouldSendSuggestions = useMemo(
    () =>
      Boolean(
        maybeSendSuggestions &&
          suggestedQuestions &&
          isSuggestedIntroQuestionsEnabled,
      ),
    [
      maybeSendSuggestions,
      suggestedQuestions,
      isSuggestedIntroQuestionsEnabled,
    ],
  );

  const payload = useMemo(() => {
    const result: AddMessagePayload[] = [];
    if (shouldSendIntro) {
      result.push({
        body: { mainText: introMessage as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.INTRO,
      });
    }
    if (shouldSendLegal) {
      result.push({
        body: { mainText: legalMessage as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.LEGAL,
      });
      result.push({
        body: { mainText: t('app.widget.chat.inboxLegalMessage') },
        sender: Sender.SYSTEM,
        messageType: MessageType.SITE_OWNER_NOTIFICATION,
      });
    }
    if (shouldSendSuggestions) {
      const additionalData =
        suggestedQuestions?.map(({ text: title }) => ({
          type: 'suggested_question',
          title,
        })) ?? [];
      const mainText = suggestionsIntroMessage || '';

      result.push({
        body: {
          mainText,
          additionalData,
        },
        sender: Sender.ASSISTANT,
        messageType: MessageType.SUGGESTED_QUESTION,
      });
    }
    if (shouldSendOfflineMessage) {
      result.push({
        body: { mainText: offlineMessage as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.OFFLINE,
      });
    }
    if (shouldSendContact) {
      result.push({
        body: { mainText: t('app.widget.form.offlineBeforeText') },
        sender: Sender.ASSISTANT,
        messageType: MessageType.CONTACT_FORM,
      });
    }
    return result;
  }, [
    shouldSendIntro,
    shouldSendLegal,
    shouldSendContact,
    shouldSendOfflineMessage,
    shouldSendSuggestions,
    suggestedQuestions,
    suggestionsIntroMessage,
    offlineMessage,
    introMessage,
    legalMessage,
    t,
  ]);

  useEffect(() => {
    if (shouldFullfillSequence && payload.length && !isEditor) {
      setIsSequenceFulfilled(false);
      dispatch(addMessage(payload)).finally(() => {
        setIsSequenceFulfilled(true);
      });
    }
  }, [shouldFullfillSequence, payload, dispatch, isEditor]);

  return {
    isSequenceFulfilled,
  };
};
